import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { AssignmentMethod } from '../../models/Support/SupportEnums';
import { emptyOnForbidden, supportQueryRetry } from '../../utils/Support/supportHelpers';
import { SUPPORT_PATHS, SUPPORT_QUERY_KEYS, unwrapValues } from './supportQueryHelpers';

export interface ISupportMailbox {
	id: string;
	name: string;
	emailAddress: string;
	isActive: boolean;
	signature?: string;
	aliasAddresses?: string[];
	roundRobinAgentIds?: string[];
	fromName?: string;
	replyToAddress?: string;
	isRoutingEnabled?: boolean;
	assignmentMethod?: AssignmentMethod;
	defaultMaxTicketsPerAgent?: number;
	reopenWindowDays?: number;
	/** When false, outbound reply/forward is blocked server-side (pre-cutover send gate). */
	sendingEnabled?: boolean;
	/**
	 * Admin-only access claims, not assignments. Omit on update to preserve existing roles;
	 * returned claims are server-normalized to `Levitate:*`.
	 */
	allowedRoles?: string[];
}

export function useSupportMailboxQuery(mailboxId: string) {
	const userSession = useUserSession();
	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.mailboxes.detail(mailboxId),
		staleTime: 30_000,
		// Don't thrash on a 403 (non-agent) — let the consumer surface the friendly state immediately.
		retry: supportQueryRetry,
		queryFn: () =>
			userSession.webServiceHelper.callAsync<ISupportMailbox>(SUPPORT_PATHS.mailboxes.detail(mailboxId), 'GET'),
		enabled: !!mailboxId,
		meta: { toastErrorMessage: 'Failed to load mailbox' },
	});
}

export function useUpdateSupportMailboxMutation(mailboxId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (mailbox: Partial<ISupportMailbox>) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.mailboxes.detail(mailboxId), 'PUT', mailbox),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.detail(mailboxId) });
			// Also invalidate the management list so renamed / toggled-active mailboxes reflect immediately.
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.lists() });
		},
	});
}

/** Fields accepted when creating a mailbox (`POST support/mailboxes`). */
export type CreateSupportMailboxBody = Pick<ISupportMailbox, 'name' | 'emailAddress'> &
	Partial<
		Pick<
			ISupportMailbox,
			| 'aliasAddresses'
			| 'signature'
			| 'fromName'
			| 'replyToAddress'
			| 'isRoutingEnabled'
			| 'assignmentMethod'
			| 'defaultMaxTicketsPerAgent'
			| 'reopenWindowDays'
		>
	>;

/**
 * Lists mailboxes the caller is an agent on (`GET support/mailboxes`).
 * Returns an empty list (not a 403) when the caller has no memberships.
 */
export function useSupportMailboxListQuery(options?: { enabled?: boolean }) {
	const userSession = useUserSession();
	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.mailboxes.lists(),
		staleTime: 30_000,
		retry: supportQueryRetry,
		enabled: options?.enabled ?? true,
		queryFn: () =>
			emptyOnForbidden<ISupportMailbox[]>(async () => {
				// API returns a bare array; unwrap defensively in case it is paged.
				const result = await userSession.webServiceHelper.callAsync<ISupportMailbox[] | { values?: ISupportMailbox[] }>(
					SUPPORT_PATHS.mailboxes.list,
					'GET'
				);
				return unwrapValues(result);
			}, []),
		meta: { toastErrorMessage: 'Failed to load mailboxes' },
	});
}

/** Creates a mailbox (`POST support/mailboxes`, account-administrator only). */
export function useCreateSupportMailboxMutation() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (mailbox: CreateSupportMailboxBody) =>
			userSession.webServiceHelper.callAsync<ISupportMailbox>(SUPPORT_PATHS.mailboxes.list, 'POST', mailbox),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.lists() });
		},
	});
}

/**
 * Deactivates (soft-deletes) a mailbox (`DELETE support/mailboxes/{id}`, mailbox-admin only).
 * The API sets `IsActive = false` rather than hard-deleting.
 */
export function useDeleteSupportMailboxMutation() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (mailboxId: string) =>
			userSession.webServiceHelper.callAsync<ISupportMailbox>(SUPPORT_PATHS.mailboxes.detail(mailboxId), 'DELETE'),
		onSuccess: (_data, mailboxId) => {
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.lists() });
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.detail(mailboxId) });
		},
	});
}
